// Copied from kettle-platform workers/admin/ui/src/charts/Delta.tsx (2026-10-30); restyled for Estra, see docs/kettle-sharing.md.
// A small comparison mark: an arrow and a whole-number % change, coloured by meaning (`good`
// says which way is better: accent when better, danger when worse, muted under 1% or when
// neither way is better). Nothing when either side is missing. The title gives both numbers.
// `Unusual` flags a value far from the usual day (the median of the 7 days before).
import type { Trend } from "../fmt";

type Good = "up" | "down" | "none";

export function Delta({ now, prev, good, format, label = "the 24h before", prefix }: {
  now: number | null | undefined;
  prev: number | null | undefined;
  good: Good;
  format: (n: number) => string;
  label?: string;
  prefix?: string;
}) {
  if (now === null || now === undefined || prev === null || prev === undefined) return null;
  const change = prev === 0 ? (now === 0 ? 0 : Infinity) : (now - prev) / prev;
  const flat = Math.abs(change) < 0.01;
  const up = change > 0;
  const cls = flat || good === "none" ? "flat" : up === (good === "up") ? "good" : "bad";
  const text = change === Infinity ? "new" : `${Math.round(Math.abs(change) * 100)}%`;
  const title = `${format(now)} vs ${format(prev)} ${label}`;
  return (
    <span className={`delta ${cls}`} title={title} aria-label={`${flat ? "about the same" : up ? "up" : "down"} ${text === "new" ? "from zero" : text}: ${title}`}>
      {prefix && <span className="delta-pre">{prefix}</span>}
      <span aria-hidden="true">{flat ? "–" : up ? "▲" : "▼"}</span>
      {!flat && <span aria-hidden="true">{text}</span>}
    </span>
  );
}

/** Today vs the 24h before, from a trend (or nothing). */
export const vsPrev = (t: Trend | null | undefined, good: Good, format: (n: number) => string) => <Delta now={t?.now} prev={t?.prev} good={good} format={format} />;

/**
 * "3.1× usual" when the last 24h is at least twice, or at most half, the usual day, and the
 * larger of the two is at least `min` (so a project going from 1 user to 3 is not flagged).
 */
export function Unusual({ t, good, format, min }: { t: Trend | null | undefined; good: Good; format: (n: number) => string; min: number }) {
  const now = t?.now;
  const usual = t?.usual;
  if (now == null || usual == null || Math.max(now, usual) < min) return null;
  const ratio = usual === 0 ? Infinity : now / usual;
  if (ratio < 2 && ratio > 0.5) return null;
  const up = ratio >= 2;
  const cls = good === "none" ? "flat" : up === (good === "up") ? "good" : "bad";
  const text = ratio === Infinity ? "new" : `${ratio >= 10 ? Math.round(ratio) : ratio.toFixed(1)}× usual`;
  return (
    <span className={`unusual ${cls}`} title={`${format(now)} now; usual (the median day of the 7 before) is ${format(usual)}`}>
      {text}
    </span>
  );
}
